import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import StatusPill from '../components/StatusPill'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { fmtDate, fmtDuration, isGeneralJob, isSuspensionJob, JOB_FINANCIAL_SELECT, jobFinancials, jobHasApprovedQuote, jobModuleLabel, jobPaymentMeta, jobProgressMeta, jobServiceScopeSummary, jobsBookedOn, bookingJobSummary, bookingComponentDraft, jobStatusMeta, localDateString, normaliseJobStatus } from '../lib/utils'
import { byLastName, newestJobFirst, workshopRowsResult } from '../lib/customerDirectory'
import {
  bookingRequestPricingSummary,
  generalServices,
  normaliseBookingServiceSnapshot,
  serviceModuleKey,
  suspensionServices,
} from '../lib/serviceMenu'
import CustomerModal from '../components/CustomerModal'
import ComponentModal from '../components/ComponentModal'
import JobModal from '../components/JobModal'
import NewJobIntakeModal from '../components/NewJobIntakeModal'
import ViewJobModal from '../components/ViewJobModal'
import {
  CALENDAR_RANGE_DAYS,
  MODULE_CALENDAR_TONES,
  addCalendarDays,
  buildCalendarEntries,
  buildCapacitySummary,
  calendarRange,
  filterCalendarEntries,
  formatPlannedMinutes,
  needsSchedulingJobs,
  plannedMinutesForBooking,
  plannedMinutesForJob,
  technicianColour,
  technicianForJob,
  technicianName,
} from '../lib/capacityPlanning'

export default function Dashboard({ initialCalendarOpen = false }) {
  const { workshopId, workshop, profile } = useAuth()
  const isOwner = profile?.role === 'owner' || profile?.is_superadmin
  const generalWorkshopEnabled = workshop?.general_workshop_enabled === true
  const [customers, setCustomers] = useState([])
  const [bikes, setBikes] = useState([])
  const [components, setComponents] = useState([])
  const [jobs, setJobs] = useState([])
  const [services, setServices] = useState([])
  const [generalJobServices, setGeneralJobServices] = useState([])
  const [team, setTeam] = useState([])
  const [bookingRequests, setBookingRequests] = useState([])
  const [bookingHistory, setBookingHistory] = useState([])
  const [showRejectedHistory, setShowRejectedHistory] = useState(false)
  const [showAcceptedHistory, setShowAcceptedHistory] = useState(false)
  const [bookingError, setBookingError] = useState(null)
  const [bookingNotice, setBookingNotice] = useState(null)
  const [busyRequestId, setBusyRequestId] = useState(null)
  const [declineRequest, setDeclineRequest] = useState(null)
  const [declineForm, setDeclineForm] = useState({ reason: '', proposedDate: '' })
  const [declineError, setDeclineError] = useState(null)
  const [declining, setDeclining] = useState(false)
  const [loading, setLoading] = useState(true)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [showCustomerModal, setShowCustomerModal] = useState(false)
  const [showJobModal, setShowJobModal] = useState(false)
  const [compModal, setCompModal] = useState(null)
  const [componentLinkError, setComponentLinkError] = useState(null)
  const [editJob, setEditJob] = useState(null)
  const [viewJobId, setViewJobId] = useState(null)
  const [reminderBusyJobId, setReminderBusyJobId] = useState(null)
  const [reminderNotice, setReminderNotice] = useState(null)
  const [reminderError, setReminderError] = useState(null)
  const [dashboardPanels, setDashboardPanels] = useState({})
  const [queueFilter, setQueueFilter] = useState('all')
  const [calendarStartDate, setCalendarStartDate] = useState(() => localDateString())
  const [calendarRangeDays, setCalendarRangeDays] = useState(CALENDAR_RANGE_DAYS.twoWeeks)
  const [showCalendar, setShowCalendar] = useState(initialCalendarOpen)
  const [calendarModules, setCalendarModules] = useState({ general: true, suspension: true })
  const [calendarTechnicianFilter, setCalendarTechnicianFilter] = useState('all')
  const [calendarBookingDetail, setCalendarBookingDetail] = useState(null)
  const [calendarFullscreen, setCalendarFullscreen] = useState(false)
  const calendarOverlayRef = useRef(null)

  const load = useCallback(async () => {
    if (!workshopId) return
    setLoadError('')
    const [c, b, co, j, j2, teamRes, br, bh] = await Promise.all([
      workshopRowsResult(supabase, 'customers', workshopId, '*', byLastName),
      workshopRowsResult(supabase, 'bikes', workshopId),
      workshopRowsResult(supabase, 'components', workshopId),
      workshopRowsResult(supabase, 'jobs', workshopId, JOB_FINANCIAL_SELECT, newestJobFirst),
      supabase.from('services').select('*').eq('workshop_id', workshopId).order('sort_order'),
      supabase.from('profiles').select('id, full_name, role, weekly_bookable_hours, calendar_colour').eq('workshop_id', workshopId).order('full_name'),
      supabase.from('booking_requests').select('*').eq('workshop_id', workshopId).eq('status', 'pending').order('created_at', { ascending: false }),
      supabase.from('booking_requests').select('*').eq('workshop_id', workshopId).neq('status', 'pending').order('created_at', { ascending: false }).limit(12)
    ])
    const readError = [c, b, co, j, j2, teamRes, br, bh].find(result => result?.error)?.error
    if (readError) {
      setLoadError(readError.message || 'Could not load the workshop.')
      setLoading(false)
      return
    }
    setCustomers(c.data || [])
    setBikes(b.data || [])
    setComponents(co.data || [])
    setJobs(j.data || [])
    const serviceRows = j2?.data || []
    setServices(suspensionServices(serviceRows))
    setGeneralJobServices(generalServices(serviceRows))
    setTeam(teamRes?.data || [])
    setBookingRequests(br?.data || [])
    setBookingHistory(bh?.data || [])
    setHasLoaded(true)
    setLoading(false)
  }, [workshopId])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (typeof document === 'undefined') return undefined
    const handleFullscreenChange = () => {
      setCalendarFullscreen(document.fullscreenElement === calendarOverlayRef.current)
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  const todayStr = localDateString()
  const todayJobs = jobsBookedOn(jobs, todayStr)
  const calendarDays = calendarRange(calendarStartDate, calendarRangeDays)
  const calendarDaySet = useMemo(() => new Set(calendarDays), [calendarDays])
  const calendarEndDate = calendarDays[calendarDays.length - 1] || calendarStartDate
  const calendarHasGeneral = generalWorkshopEnabled || jobs.some(isGeneralJob) || bookingRequests.some(bookingRequestHasGeneralService)
  const activeCalendarModules = {
    general: calendarHasGeneral ? calendarModules.general : false,
    suspension: calendarModules.suspension,
  }
  const calendarServiceRows = useMemo(() => [...services, ...generalJobServices], [services, generalJobServices])
  const calendarEntries = useMemo(() => buildCalendarEntries({
    jobs,
    bookingRequests,
    customers,
    bikes,
    components,
    team,
    services: calendarServiceRows,
  }), [jobs, bookingRequests, customers, bikes, components, team, calendarServiceRows])
  const visibleCalendarEntries = useMemo(() => filterCalendarEntries(
    calendarEntries.filter(entry => calendarDaySet.has(entry.date)),
    { modules: activeCalendarModules, technician: calendarTechnicianFilter }
  ), [calendarEntries, calendarDaySet, activeCalendarModules, calendarTechnicianFilter])
  const calendarEntriesByDate = useMemo(() => visibleCalendarEntries.reduce((groups, entry) => {
    if (!groups[entry.date]) groups[entry.date] = []
    groups[entry.date].push(entry)
    return groups
  }, {}), [visibleCalendarEntries])
  const calendarJobs = visibleCalendarEntries.filter(entry => entry.type === 'job').map(entry => entry.job)
  const calendarJobCount = calendarJobs.length
  const calendarGeneralJobCount = calendarJobs.filter(isGeneralJob).length
  const calendarSuspensionJobCount = calendarJobs.filter(isSuspensionJob).length
  const calendarPendingCount = visibleCalendarEntries.filter(entry => entry.type === 'booking').length
  const calendarNeedsScheduling = needsSchedulingJobs(jobs, { services: calendarServiceRows }).filter(job => {
    const moduleKey = isGeneralJob(job) ? 'general' : 'suspension'
    if (activeCalendarModules[moduleKey] === false) return false
    if (calendarTechnicianFilter === 'all') return true
    const technician = technicianForJob(job, team)
    if (calendarTechnicianFilter === 'unassigned') return !technician
    return String(technician?.id || job.technician_id || '') === String(calendarTechnicianFilter)
  })
  const calendarNeedsSchedulingGeneralCount = calendarNeedsScheduling.filter(isGeneralJob).length
  const calendarNeedsSchedulingSuspensionCount = calendarNeedsScheduling.filter(isSuspensionJob).length
  const calendarNeedsComponentCount = calendarJobs.filter(j => isSuspensionJob(j) && !j.component_id).length
  const calendarUnassignedTechCount = calendarJobs.filter(j => !technicianForJob(j, team)).length
  const calendarValue = calendarJobs.reduce((sum, j) => sum + jobFinancials(j).totalCost, 0)
  const capacityWeeks = useMemo(() => buildCapacitySummary({
    jobs,
    bookingRequests,
    workshop,
    team,
    startDate: calendarStartDate,
    dayCount: calendarRangeDays,
    services: calendarServiceRows,
  }), [jobs, bookingRequests, workshop, team, calendarStartDate, calendarRangeDays, calendarServiceRows])
  const serviceDueCutoff = addDays(todayStr, 30)
  const serviceDueAll = jobs
    .filter(j => isSuspensionJob(j) && j.next_service_date && j.next_service_date <= serviceDueCutoff)
    .sort((a, b) => {
      const aOverdue = a.next_service_date < todayStr
      const bOverdue = b.next_service_date < todayStr
      if (aOverdue && bOverdue) return b.next_service_date.localeCompare(a.next_service_date)
      return a.next_service_date.localeCompare(b.next_service_date)
    })
  const serviceDueJobs = serviceDueAll
    .slice(0, 8)
  const rev = jobs.reduce((s, j) => s + jobFinancials(j).totalCost, 0)
  const margin = jobs.reduce((s, j) => s + jobFinancials(j).grossMargin, 0)
  const mpct = rev > 0 ? ((margin / rev) * 100).toFixed(0) : 0
  const workflowBreakdown = [
    { key: 'awaiting_customer_item', label: 'Awaiting item', tone: 'orange' },
    { key: 'received', label: 'Received', tone: 'blue' },
    { key: 'estimate_sent', label: 'Quote sent', tone: 'orange' },
    { key: 'awaiting_parts', label: 'Awaiting parts', tone: 'orange' },
    { key: 'ready_for_collection', label: 'Ready collection', tone: 'green' },
    { key: 'completed', label: 'Completed', tone: 'green' },
    { key: 'cancelled', label: 'Cancelled', tone: 'muted' },
  ].map(row => ({
    ...row,
    count: jobs.filter(j => normaliseJobStatus(j.status) === row.key).length,
  }))
  const paymentBreakdown = ['not_requested', 'requested', 'paid', 'failed', 'cancelled'].map(key => {
    const meta = jobPaymentMeta({ payment_status: key })
    return {
      key,
      label: meta.label,
      tone: meta.tone,
      count: jobs.filter(j => jobPaymentMeta(j).key === key).length,
    }
  })
  const waitingPaymentCount = paymentBreakdown.find(row => row.key === 'requested')?.count || 0

  function avgTime(filterFn) {
    const relevant = jobs.filter(j => {
      const comp = components.find(c => c.id === j.component_id)
      return isSuspensionJob(j) && comp && filterFn(comp) && j.duration_mins > 0
    })
    if (!relevant.length) return null
    return Math.round(relevant.reduce((s, j) => s + j.duration_mins, 0) / relevant.length)
  }

  const timeKpis = [
    ['Avg Fork Service', avgTime(c => c.type === 'fork')],
    ['Avg Shock Service', avgTime(c => c.type === 'shock')],
    ['Avg Dropper Service', avgTime(c => c.type === 'dropper')],
    ['Avg Fox Job', avgTime(c => c.brand === 'fox')],
    ['Avg RockShox Job', avgTime(c => c.brand === 'rockshox')],
  ].filter(([, v]) => v)

  const recentJobs = [...jobs].slice(0, 6)

  const bySpend = customers.map(c => ({
    name: c.first_name + ' ' + c.last_name,
    spend: jobs.filter(j => j.customer_id === c.id).reduce((s, j) => s + jobFinancials(j).totalCost, 0),
    count: jobs.filter(j => j.customer_id === c.id).length
  })).sort((a, b) => b.spend - a.spend).slice(0, 6)

  const activeJobs = jobs.filter(isActiveJob)
  const suspensionActiveJobs = activeJobs.filter(isSuspensionJob)
  const overdueActiveJobs = activeJobs.filter(j => (j.due_date || j.date) && (j.due_date || j.date) < todayStr)
  const awaitingItemJobs = activeJobs.filter(j => workflowKey(j) === 'awaiting_customer_item')
  const receivedJobs = activeJobs.filter(j => workflowKey(j) === 'received')
  const quoteWaitingJobs = activeJobs.filter(j => workflowKey(j) === 'estimate_sent' && !jobHasApprovedQuote(j))
  const quoteApprovedJobs = activeJobs.filter(jobHasApprovedQuote)
  const awaitingPartsJobs = activeJobs.filter(j => workflowKey(j) === 'awaiting_parts')
  const needsComponentJobs = suspensionActiveJobs.filter(j => !j.component_id)
  const qcAttentionJobs = activeJobs.filter(j => {
    const qc = progressFor(j, 'qc')
    return ['Started', 'Ready to sign', 'Failed'].includes(qc?.value)
  })
  const reportNeededJobs = activeJobs.filter(j => j.qc_signed_off_at && !j.report_sent_at)
  const readyCollectionJobs = activeJobs.filter(j => workflowKey(j) === 'ready_for_collection')
  const waitingPaymentJobs = activeJobs.filter(j => jobPaymentMeta(j).key === 'requested')
  const paymentNotSentReadyJobs = readyCollectionJobs.filter(j => jobPaymentMeta(j).key === 'not_requested')
  const blockedJobs = uniqueJobs([...overdueActiveJobs, ...needsComponentJobs, ...awaitingItemJobs, ...quoteWaitingJobs, ...awaitingPartsJobs])
  const workActionJobs = uniqueJobs([...receivedJobs, ...quoteApprovedJobs, ...qcAttentionJobs, ...reportNeededJobs])
  const readyPaymentJobs = uniqueJobs([...readyCollectionJobs, ...waitingPaymentJobs, ...paymentNotSentReadyJobs])
  const focusBlockerCount = blockedJobs.length
  const focusNeedsWorkCount = workActionJobs.length
  const focusReadyCount = readyPaymentJobs.length
  const focusValue = activeJobs.reduce((sum, j) => sum + jobFinancials(j).totalCost, 0)
  const dashboardQueues = [
    {
      key: 'today',
      title: "Today's work",
      subtitle: `${todayJobs.length} booked today`,
      tone: todayJobs.length ? 'blue' : 'muted',
      jobs: todayJobs,
      empty: 'No jobs booked today.',
    },
    {
      key: 'blocked',
      title: 'Blocked / waiting',
      subtitle: `${focusBlockerCount} need attention`,
      tone: focusBlockerCount ? 'orange' : 'muted',
      jobs: blockedJobs,
      empty: 'No blocked jobs.',
    },
    {
      key: 'work',
      title: 'Workshop action',
      subtitle: `${focusNeedsWorkCount} to progress`,
      tone: focusNeedsWorkCount ? 'blue' : 'muted',
      jobs: workActionJobs,
      empty: 'No inspection, QC or report actions waiting.',
    },
    {
      key: 'collection',
      title: 'Ready / payment',
      subtitle: `${focusReadyCount} ready or waiting payment`,
      tone: focusReadyCount ? 'green' : 'muted',
      jobs: readyPaymentJobs,
      empty: 'Nothing waiting for payment or collection.',
    },
    {
      key: 'reminders',
      title: 'Service reminders',
      subtitle: `${serviceDueAll.length} due within 30 days`,
      tone: serviceDueAll.length ? 'orange' : 'muted',
      jobs: serviceDueJobs,
      empty: 'No services due in the next 30 days.',
      reminderQueue: true,
    },
  ]

  function getComp(j) { return components.find(c => c.id === j.component_id) }
  function getCust(j) { return customers.find(c => c.id === j.customer_id) }
  function getBike(j) { return bikes.find(b => b.id === j.bike_id) }

  function workflowKey(job) {
    return normaliseJobStatus(job?.status)
  }

  function isActiveJob(job) {
    return !['completed', 'cancelled'].includes(workflowKey(job))
  }

  function progressFor(job, key) {
    return jobProgressMeta(job).find(item => item.key === key)
  }

  function uniqueJobs(rows) {
    const seen = new Set()
    return rows
      .filter(Boolean)
      .sort((a, b) => {
        const aDate = a.due_date || a.date || '9999-12-31'
        const bDate = b.due_date || b.date || '9999-12-31'
        if (aDate !== bDate) return aDate.localeCompare(bDate)
        return (a.start_time || '99:99').localeCompare(b.start_time || '99:99')
      })
      .filter(job => {
        if (seen.has(job.id)) return false
        seen.add(job.id)
        return true
      })
  }

  function panelOpen(key, defaultOpen = true) {
    return Object.prototype.hasOwnProperty.call(dashboardPanels, key) ? dashboardPanels[key] : defaultOpen
  }

  function togglePanel(key, defaultOpen = true) {
    const scrollY = typeof window !== 'undefined' ? window.scrollY : 0
    const scrollX = typeof window !== 'undefined' ? window.scrollX : 0
    setDashboardPanels(prev => ({
      ...prev,
      [key]: !(Object.prototype.hasOwnProperty.call(prev, key) ? prev[key] : defaultOpen),
    }))
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => window.scrollTo(scrollX, scrollY))
    }
  }

  async function sendServiceReminder(job) {
    const cust = getCust(job)
    if (!cust?.email) {
      setReminderNotice(null)
      setReminderError('This customer needs an email address before a service reminder can be sent.')
      return
    }
    if (workshop?.reminders_enabled === false) {
      setReminderNotice(null)
      setReminderError('Service reminders are switched off in workshop settings.')
      return
    }

    setReminderBusyJobId(job.id)
    setReminderNotice(null)
    setReminderError(null)

    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (!token) throw new Error('Please sign in again before sending reminders.')

      const res = await fetch('/.netlify/functions/send-service-reminder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ jobId: job.id }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Service reminder failed')

      setJobs(rows => rows.map(row => row.id === job.id ? {
        ...row,
        reminder_sent: true,
        reminder_status: data.reminder_status || 'sent',
        reminder_sent_at: data.reminder_sent_at || new Date().toISOString(),
        reminder_last_attempt_at: data.reminder_sent_at || new Date().toISOString(),
        reminder_last_error: '',
        reminder_count: data.reminder_count ?? ((Number(row.reminder_count) || 0) + 1),
      } : row))
      setReminderNotice(`Service reminder sent to ${cust.email}.`)
    } catch (err) {
      setReminderError(err.message || 'Service reminder failed')
    } finally {
      setReminderBusyJobId(null)
    }
  }

  const viewJob = jobs.find(j => j.id === viewJobId)

  function statusColor(status) {
    if (status === 'accepted' || status === 'converted') return 'var(--green)'
    if (status === 'declined') return 'var(--red)'
    return 'var(--muted)'
  }

  function addDays(dateStr, amount) {
    const d = new Date(`${dateStr}T12:00:00`)
    d.setDate(d.getDate() + amount)
    return d.toISOString().slice(0, 10)
  }

  function calendarLabel(dateStr) {
    const date = new Date(`${dateStr}T12:00:00`)
    return {
      day: date.toLocaleDateString('en-GB', { weekday: 'short' }),
      date: date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    }
  }

  function shiftCalendar(amount) {
    setCalendarStartDate(current => addCalendarDays(current || todayStr, amount))
  }

  function calendarJobTitle(job) {
    const service = jobServiceScopeSummary(job)
    if (service) return service
    const comp = getComp(job)
    if (comp) return `${comp.year ? comp.year + ' ' : ''}${comp.model}`
    return bookingJobSummary(job) || 'Booking job'
  }

  function calendarJobMeta(job) {
    const cust = getCust(job)
    return [
      cust ? `${cust.first_name} ${cust.last_name}` : 'Unknown customer',
      job.start_time || null,
      job.technician || null,
    ].filter(Boolean).join(' - ')
  }

  function calendarJobComponent(job) {
    const comp = getComp(job)
    if (!comp && isGeneralJob(job)) return 'General Workshop job'
    return comp ? `${comp.year ? comp.year + ' ' : ''}${comp.model}` : 'No component linked'
  }

  function jobDisplayTitle(job) {
    return calendarJobTitle(job)
  }

  function jobStatus(job) {
    return jobStatusMeta(job)
  }

  function progressChips(job, limit = 4) {
    return jobProgressMeta(job).slice(0, limit).map(item => (
      <StatusPill key={item.key} tone={item.tone} style={{ marginRight: 4, marginTop: 4 }}>
        {item.label}: {item.value}
      </StatusPill>
    ))
  }

  function chartToneColor(tone) {
    if (tone === 'green') return 'var(--green)'
    if (tone === 'orange' || tone === 'yellow') return 'var(--yellow)'
    if (tone === 'red') return 'var(--red)'
    if (tone === 'blue') return 'var(--blue)'
    return 'var(--muted)'
  }

  function MiniBreakdownCard({ title, subtitle, rows }) {
    const total = rows.reduce((sum, row) => sum + row.count, 0)
    const max = Math.max(1, ...rows.map(row => row.count))
    return (
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="section-head compact">
          <div>
            <div className="card-title" style={{ marginBottom: 2 }}>{title}</div>
            <div className="text-muted fs12">{subtitle}</div>
          </div>
          <span className="section-meta">{total} total</span>
        </div>
        <div style={{ display: 'grid', gap: 10 }}>
          {rows.map(row => {
            const width = total ? `${Math.max(8, (row.count / max) * 100)}%` : '0%'
            return (
              <div key={row.key}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
                  <span className="fs12 text-white fw7">{row.label}</span>
                  <span className="fs12 text-muted">{row.count}</span>
                </div>
                <div style={{ height: 8, borderRadius: 999, background: 'rgba(255,255,255,.08)', overflow: 'hidden' }}>
                  <div style={{ width, height: '100%', background: chartToneColor(row.tone), borderRadius: 999 }} />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  function CollapsibleCard({ id, title, subtitle, meta, defaultOpen = true, children, actions, tone = 'muted', className = '' }) {
    const open = panelOpen(id, defaultOpen)
    return (
      <div className={`card ${className}`.trim()} style={{ marginBottom: 16, padding: 0, overflow: 'hidden' }}>
        <div
          className="section-head compact"
          style={{
            marginBottom: 0,
            padding: '14px 16px',
            borderBottom: open ? '1px solid var(--border)' : 'none',
            background: 'var(--dark)',
          }}
        >
          <button
            type="button"
            onClick={() => togglePanel(id, defaultOpen)}
            style={{
              minWidth: 0,
              flex: 1,
              border: 0,
              padding: 0,
              background: 'transparent',
              color: 'inherit',
              textAlign: 'left',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <span className={`dashboard-toggle-chip dashboard-toggle-${open ? 'hide' : 'show'}`}>{open ? 'Hide' : 'Show'}</span>
              <div style={{ minWidth: 0 }}>
                <div className="card-title" style={{ marginBottom: 2 }}>{title}</div>
                {subtitle && <div className="text-muted fs12">{subtitle}</div>}
              </div>
            </div>
          </button>
          <div className="action-row">
            {meta && <span className="section-meta">{meta}</span>}
            {actions}
          </div>
        </div>
        {open && <div style={{ padding: 16 }}>{children}</div>}
      </div>
    )
  }

  function FocusTile({ label, value, detail, tone = 'muted' }) {
    return (
      <div className="focus-summary-item">
        <span className={`job-status-chip job-status-${tone}`}>{label}</span>
        <strong>{value}</strong>
        {detail && <span>{detail}</span>}
      </div>
    )
  }

  function ActionQueueCard({ queue }) {
    const expanded = panelOpen(`queue-${queue.key}`, false)
    const previewLimit = 2
    const jobsToShow = expanded ? queue.jobs : queue.jobs.slice(0, previewLimit)
    return (
      <div className="focus-queue">
        <div className="section-head compact" style={{ marginBottom: 10 }}>
          <div style={{ minWidth: 0 }}>
            <div className="card-title" style={{ marginBottom: 2 }}>{queue.title}</div>
            <div className="text-muted fs12">{queue.subtitle}</div>
          </div>
          <span className={`job-status-chip job-status-${queue.tone}`}>{queue.jobs.length}</span>
        </div>
        {jobsToShow.length ? jobsToShow.map(job => (
          queue.reminderQueue
            ? <ReminderDueRow key={job.id} job={job} compact />
            : <ActionJobRow key={job.id} job={job} />
        )) : (
          <div className="muted-empty">{queue.empty}</div>
        )}
        {queue.jobs.length > previewLimit && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => togglePanel(`queue-${queue.key}`, false)}
            style={{ marginTop: 10, width: '100%', justifyContent: 'center' }}
          >
            {expanded ? 'Show less' : `Show all ${queue.jobs.length}`}
          </button>
        )}
      </div>
    )
  }

  function ActionJobRow({ job }) {
    const cust = getCust(job)
    const comp = getComp(job)
    const generalJob = isGeneralJob(job)
    const status = jobStatus(job)
    const payment = jobPaymentMeta(job)
    const financials = jobFinancials(job)
    const progress = jobProgressMeta(job)
    const qc = progress.find(item => item.key === 'qc')
    const report = progress.find(item => item.key === 'report')
    const dueDate = job.due_date || job.date
    const overdue = dueDate && dueDate < todayStr && isActiveJob(job)
    return (
      <div className="job-row focus-job-row">
        <div className="focus-job-main">
          <div className="focus-job-title">{jobDisplayTitle(job)}</div>
          <div className="focus-job-meta">
            {cust ? `${cust.first_name} ${cust.last_name}` : 'Unknown customer'}
            {comp ? ` - ${calendarJobComponent(job)}` : generalJob ? ' - General Workshop job' : ' - No component'}
          </div>
          <div className="focus-job-badges">
            {generalJob && <span className="job-status-chip job-status-blue">{jobModuleLabel(job)}</span>}
            <StatusPill tone={status.tone}>{status.label}</StatusPill>
            {overdue && <StatusPill tone="red">Overdue</StatusPill>}
            {qc && <StatusPill tone={qc.tone}>QC: {qc.value}</StatusPill>}
            {report && <StatusPill tone={report.tone}>Report: {report.value}</StatusPill>}
            {payment.key !== 'not_requested' && <StatusPill tone={payment.tone}>Payment: {payment.label}</StatusPill>}
          </div>
        </div>
        <div className="focus-job-actions">
          <div className="focus-job-date">
            {dueDate ? fmtDate(dueDate) : ''}
            {financials.totalCost > 0 && <div className="text-green">GBP {financials.totalCost.toFixed(0)}</div>}
          </div>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setEditJob(job)}>Open job</button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setViewJobId(job.id)}>Summary</button>
          {!generalJob && !comp && cust && <button type="button" className="btn btn-green btn-sm" onClick={() => createComponentFromJob(job)}>Component</button>}
        </div>
      </div>
    )
  }

  function ReminderDueRow({ job, compact = false }) {
    const cust = getCust(job)
    const comp = getComp(job)
    const overdue = job.next_service_date < todayStr
    const reminderSentAt = job.reminder_sent_at || (job.reminder_sent ? job.reminder_last_attempt_at : '')
    const reminderFailed = job.reminder_status === 'failed'
    const canSendReminder = workshop?.reminders_enabled !== false && cust?.email && !job.reminder_disabled
    return (
      <div key={`due-${job.id}`} className="job-row focus-job-row focus-reminder-row">
        <div className="focus-job-main">
          <div className="focus-job-title">{cust ? `${cust.first_name} ${cust.last_name}` : 'Unknown customer'}</div>
          <div className="focus-job-meta">
            {jobDisplayTitle(job)}
            {comp ? ` - ${calendarJobComponent(job)}` : ''}
            {job.next_service ? ` - ${job.next_service}` : ''}
          </div>
          <div className={`focus-reminder-note ${reminderFailed ? 'text-red' : 'text-muted'}`}>
            {reminderFailed
              ? `Reminder failed${job.reminder_last_error ? `: ${job.reminder_last_error}` : ''}`
              : reminderSentAt
                ? `Reminder sent ${fmtDate(String(reminderSentAt).slice(0, 10))}`
                : cust?.email
                  ? 'Reminder not sent yet'
                  : 'No customer email'}
          </div>
        </div>
        <div className="focus-job-actions">
          <span className={`focus-due-date ${overdue ? 'text-red' : 'text-green'}`}>
            {overdue ? 'Overdue: ' : 'Due: '}{fmtDate(job.next_service_date)}
          </span>
          {!compact && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setViewJobId(job.id)}>View</button>}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => sendServiceReminder(job)}
            disabled={!canSendReminder || reminderBusyJobId === job.id}
            title={!canSendReminder ? 'Customer email and workshop reminders are required' : 'Send this customer a service reminder email'}
          >
            {reminderBusyJobId === job.id ? 'Sending...' : reminderSentAt ? 'Send Again' : 'Send Reminder'}
          </button>
          {!compact && <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowJobModal({ customerId: job.customer_id, componentId: job.component_id })}>New Job</button>}
        </div>
      </div>
    )
  }

  function calendarTooltipText(job) {
    const financials = jobFinancials(job)
    return [
      calendarJobTitle(job),
      calendarJobMeta(job),
      calendarJobComponent(job),
      financials.totalCost > 0 ? `GBP ${financials.totalCost.toFixed(2)}` : null,
      job.notes ? job.notes.split('\n').slice(0, 3).join(' - ') : null,
    ].filter(Boolean).join('\n')
  }

  function createComponentFromJob(job) {
    setComponentLinkError(null)
    setCompModal({
      customerId: job.customer_id,
      jobId: job.id,
      prefillData: bookingComponentDraft(job),
    })
  }

  async function handleComponentSaved(component) {
    const jobId = compModal?.jobId
    setComponentLinkError(null)

    if (jobId && component?.id) {
      const { error } = await supabase
        .from('jobs')
        .update({ component_id: component.id })
        .eq('id', jobId)
      if (error) setComponentLinkError(error.message)
    }

    setCompModal(null)
    await load()
  }

  function bikeProfile(row) {
    return [row?.bike_year, row?.bike_brand, row?.bike_model].filter(Boolean).join(' ')
  }

  function bikeSetupMeta(row) {
    return [
      row?.bike_type ? `Category: ${row.bike_type}` : null,
      row?.bike_size ? `Size: ${row.bike_size}` : null,
      row?.wheel_setup ? `Wheel: ${row.wheel_setup}` : null,
    ].filter(Boolean).join(' - ')
  }

  const acceptedBookingHistory = bookingHistory.filter(req => req.status !== 'declined')
  const rejectedBookings = bookingHistory.filter(req => req.status === 'declined')
  const activeServices = services.filter(service => service.active !== false)
  const launchSteps = [
    {
      key: 'details',
      label: 'Workshop details',
      done: Boolean(workshop?.name && (workshop?.booking_notification_email || workshop?.email)),
      action: () => window.location.href = '/settings?tab=workshop',
      actionLabel: 'Workshop'
    },
    {
      key: 'services',
      label: 'Service menu',
      done: activeServices.length > 0,
      action: () => window.location.href = '/settings?tab=services',
      actionLabel: 'Services'
    },
    {
      key: 'slug',
      label: 'Booking slug',
      done: Boolean(workshop?.slug),
      action: () => window.location.href = '/settings?tab=workshop',
      actionLabel: 'Booking'
    },
    {
      key: 'capacity',
      label: 'Daily capacity',
      done: Number(workshop?.max_jobs_per_day || 0) > 0,
      action: () => window.location.href = '/settings?tab=workshop',
      actionLabel: 'Capacity'
    }
  ]
  const queueFilterOptions = [
    ['all', 'All queues'],
    ['today', 'Today'],
    ['blocked', 'Blocked'],
    ['collection', 'Ready/payment'],
    ['reminders', 'Service due'],
  ]
  const visibleDashboardQueues = queueFilter === 'all'
    ? dashboardQueues
    : dashboardQueues.filter(queue => queue.key === queueFilter)
  const launchDoneCount = launchSteps.filter(step => step.done).length
  const showLaunchSetup = isOwner && launchDoneCount < launchSteps.length
  const nextLaunchStep = launchSteps.find(step => !step.done)

  function bookingPreview(req) {
    return [
      req.service_summary ? req.service_summary.split('\n').filter(Boolean).join(' - ') : null,
      bikeProfile(req) ? `Bike: ${bikeProfile(req)}` : null,
      bikeSetupMeta(req) ? `Bike setup: ${bikeSetupMeta(req)}` : null,
      req.bike_details ? `Bike details: ${req.bike_details}` : null,
      req.component_details ? req.component_details.split('\n').filter(Boolean).join(' - ') : null,
      req.notes || null,
    ].filter(Boolean)[0] || 'Booking request'
  }

  function bookingRequestServices(req) {
    return normaliseBookingServiceSnapshot(req.service_snapshot)
  }

  function bookingRequestHasGeneralService(req) {
    return bookingRequestServices(req)
      .some(service => serviceModuleKey(service) === 'general')
  }

  function bookingRequestHasSuspensionService(req) {
    return bookingRequestServices(req)
      .some(service => serviceModuleKey(service) === 'suspension')
  }

  function bookingRequestIsGeneralOnly(req) {
    return bookingRequestHasGeneralService(req) && !bookingRequestHasSuspensionService(req)
  }

  function bookingRequestSuspensionAcceptBlockReason(req) {
    const pricing = bookingRequestPricingSummary(req)
    if (bookingRequestHasGeneralService(req)) {
      return bookingRequestIsGeneralOnly(req)
        ? 'General bookings can be converted using the General Workshop job flow.'
        : 'Mixed Suspension and General bookings can be reviewed here, but split job creation is not available yet.'
    }
    if (pricing.pricing_mode !== 'fixed') {
      return 'Quote-based bookings can be reviewed here, but job creation is only available once pricing is final.'
    }
    return ''
  }

  function generalBookingConversionBlockReason(req) {
    if (!bookingRequestIsGeneralOnly(req)) {
      return bookingRequestHasGeneralService(req)
        ? 'Mixed Suspension and General bookings can be reviewed here, but split job creation is not available yet.'
        : ''
    }
    if (!generalWorkshopEnabled) {
      return 'General Workshop job creation is disabled for this workshop.'
    }
    if (req.converted_job_id) {
      return 'This booking request has already been converted into a job.'
    }
    return ''
  }

  function bookingRequestReviewReason(req) {
    return bookingRequestIsGeneralOnly(req)
      ? generalBookingConversionBlockReason(req)
      : bookingRequestSuspensionAcceptBlockReason(req)
  }

  function openDeclineModal(req) {
    setDeclineRequest(req)
    setDeclineForm({ reason: '', proposedDate: '' })
    setDeclineError(null)
  }

  function closeDeclineModal() {
    setDeclineRequest(null)
    setDeclineForm({ reason: '', proposedDate: '' })
    setDeclineError(null)
  }

  function setDeclineField(field, value) {
    setDeclineForm(prev => ({ ...prev, [field]: value }))
  }

  async function acceptBookingWithTrustedPath(req, successMessage, options = {}) {
    setBusyRequestId(req.id)
    setBookingError(null)
    setBookingNotice(null)
    try {
      const { data, error } = await supabase.rpc('accept_service_booking', {
        p_booking_request_id: req.id,
        p_note: '',
      })
      if (error) throw error

      await load()
      if (options.openJob && data?.job_id) setViewJobId(data.job_id)
      setBookingNotice(data?.idempotent
        ? 'Booking was already accepted - existing job opened safely.'
        : successMessage)
    } catch (err) {
      setBookingError(err.message || 'Could not accept this booking request.')
    }
    setBusyRequestId(null)
  }

  async function acceptBookingRequest(req) {
    const blockReason = bookingRequestSuspensionAcceptBlockReason(req)
    if (blockReason) {
      setBookingError(blockReason)
      setBookingNotice(null)
      return
    }

    await acceptBookingWithTrustedPath(req, 'Booking accepted - draft job added to All Service Jobs.')
  }

  async function convertGeneralBookingRequest(req) {
    const blockReason = generalBookingConversionBlockReason(req)
    if (blockReason) {
      setBookingError(blockReason)
      setBookingNotice(null)
      return
    }

    await acceptBookingWithTrustedPath(
      req,
      'General booking converted - General job added to All Service Jobs.',
      { openJob: true }
    )
  }

  async function declineBookingRequest(e) {
    e.preventDefault()
    if (!declineRequest) return

    const reason = declineForm.reason.trim()
    if (!reason) {
      setDeclineError('Add a reason before declining the booking.')
      return
    }

    setBusyRequestId(declineRequest.id)
    setDeclining(true)
    setDeclineError(null)
    setBookingError(null)
    setBookingNotice(null)

    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (!token) throw new Error('Please sign in again before declining this booking.')

      const res = await fetch('/.netlify/functions/respond-booking-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          requestId: declineRequest.id,
          status: 'declined',
          reason,
          proposedDate: declineForm.proposedDate || null
        })
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Could not decline booking request.')

      await load()
      setBookingNotice(data.email_sent
        ? 'Booking declined and the customer has been emailed.'
        : 'Booking declined. Customer email was not sent, so check the booking notification setup.')
      closeDeclineModal()
    } catch (err) {
      setDeclineError(err.message || 'Could not decline booking request.')
    } finally {
      setDeclining(false)
      setBusyRequestId(null)
    }
  }

  function toggleCalendarModule(moduleKey) {
    setCalendarModules(current => ({
      ...current,
      [moduleKey]: current[moduleKey] === false,
    }))
  }

  function openCalendarEntry(entry) {
    if (entry.type === 'job') {
      setCalendarBookingDetail(null)
      setShowCalendar(true)
      setViewJobId(entry.job.id)
      return
    }
    setShowCalendar(true)
    setCalendarBookingDetail(entry.request)
  }

  function calendarModuleShortLabel(job) {
    return isGeneralJob(job) ? 'General' : 'Suspension'
  }

  function calendarSchedulingMeta(job) {
    const missing = []
    if (!job.scheduled_date) missing.push('booked-in date')
    if (!job.technician_id) missing.push('technician')
    if (!plannedMinutesForJob(job, { services: calendarServiceRows })) missing.push('planned time')
    return missing.length ? `Needs ${missing.join(', ')}` : 'Scheduled'
  }

  function calendarEntryTooltip(entry) {
    return [
      entry.title,
      entry.subtitle,
      entry.provisional ? 'Provisional booking request' : 'Confirmed job',
    ].filter(Boolean).join('\n')
  }

  function capacityMinuteLabel(value) {
    const mins = Math.round(Number(value || 0))
    if (mins === 0) return '0m'
    return `${mins < 0 ? '-' : ''}${fmtDuration(Math.abs(mins))}`
  }

  function capacityStatusText(status) {
    if (status === 'over') return 'Over capacity'
    if (status === 'nearly') return 'Nearly full'
    return 'Normal'
  }

  async function toggleCalendarFullscreen() {
    if (typeof document === 'undefined') return
    const element = calendarOverlayRef.current
    if (!element?.requestFullscreen) return
    try {
      if (document.fullscreenElement) await document.exitFullscreen()
      else await element.requestFullscreen()
    } catch {}
  }

  function CalendarEntry({ entry }) {
    const status = entry.type === 'job' ? jobStatus(entry.job) : { label: 'Pending', tone: 'orange' }
    const moduleTone = MODULE_CALENDAR_TONES[entry.moduleKey] || 'muted'
    const techLabel = entry.technician ? technicianName(entry.technician) : 'Unassigned'
    const techColour = entry.technician ? entry.technicianColour : ''
    return (
      <button
        type="button"
        className={`calendar-entry calendar-entry-${entry.type} calendar-entry-module-${moduleTone}${entry.provisional ? ' provisional' : ''}`}
        onClick={() => openCalendarEntry(entry)}
        title={calendarEntryTooltip(entry)}
      >
        <span className="calendar-entry-head">
          <strong>{entry.title}</strong>
          <em>{entry.moduleKey === 'general' ? 'General' : 'Suspension'}</em>
        </span>
        <span className="calendar-entry-meta">{entry.subtitle}</span>
        <span className="calendar-entry-foot">
          <StatusPill tone={status.tone}>{entry.provisional ? 'Provisional' : status.label}</StatusPill>
          <span className="calendar-tech-badge">
            <span className="calendar-tech-dot" style={techColour ? { background: techColour } : undefined} />
            {techLabel}
          </span>
        </span>
      </button>
    )
  }

  function CalendarSideJobRow({ job }) {
    return (
      <button key={job.id} type="button" className="calendar-side-row" onClick={() => openCalendarEntry({ type: 'job', job, id: job.id })}>
        <span className="calendar-side-row-head">
          <strong>{calendarJobTitle(job)}</strong>
          <em>{calendarModuleShortLabel(job)}</em>
        </span>
        <span>
          {!job.scheduled_date ? 'No booked-in date' : fmtDate(job.scheduled_date)}
          {' - '}
          {calendarSchedulingMeta(job)}
          {' - Open schedule'}
        </span>
      </button>
    )
  }

  function CalendarLaunchCard() {
    const previewEntries = visibleCalendarEntries.slice(0, 4)
    return (
      <CollapsibleCard
        id="upcoming-calendar"
        title="Workshop Calendar"
        subtitle={`${fmtDate(calendarStartDate)} to ${fmtDate(calendarEndDate)}. Open the planner for jobs, bookings and weekly capacity.`}
        meta={`${calendarGeneralJobCount} General, ${calendarSuspensionJobCount} Suspension, ${calendarPendingCount} provisional`}
        tone={calendarJobCount || calendarPendingCount ? 'blue' : 'muted'}
        defaultOpen={false}
        actions={<button type="button" className="btn btn-primary btn-sm" onClick={() => setShowCalendar(true)}>Open Calendar</button>}
      >
        <div className="calendar-summary" style={{ justifyContent: 'flex-start', marginBottom: 14, maxWidth: 'none' }}>
          <span>General <strong>{calendarGeneralJobCount}</strong></span>
          <span>Suspension <strong>{calendarSuspensionJobCount}</strong></span>
          <span>Needs component <strong>{calendarNeedsComponentCount}</strong></span>
          <span>Unassigned <strong>{calendarUnassignedTechCount}</strong></span>
          <span>Needs scheduling <strong>{calendarNeedsScheduling.length}</strong> <em>{calendarNeedsSchedulingGeneralCount} G / {calendarNeedsSchedulingSuspensionCount} S</em></span>
          <span>Value <strong>GBP {calendarValue.toFixed(0)}</strong></span>
        </div>
        {previewEntries.length > 0 && (
          <div className="calendar-mini-list">
            {previewEntries.map(entry => (
              <button key={`${entry.type}-${entry.id}`} type="button" className="calendar-mini-entry" onClick={() => openCalendarEntry(entry)}>
                <span>{fmtDate(entry.date)}</span>
                <strong>{entry.title}</strong>
                <em>{entry.moduleKey === 'general' ? 'General' : entry.provisional ? 'Provisional' : 'Suspension'}</em>
              </button>
            ))}
          </div>
        )}
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowCalendar(true)}>Open planning overlay</button>
      </CollapsibleCard>
    )
  }

  function renderCalendarOverlay() {
    const fullscreenSupported = typeof document !== 'undefined' && typeof document.documentElement?.requestFullscreen === 'function'
    const pendingRows = bookingRequests.filter(request => request.status === 'pending')
    const calendarPage = initialCalendarOpen

    return (
      <div className={`calendar-overlay calendar-refresh-page${calendarPage ? ' calendar-route-page' : ''}`} ref={calendarOverlayRef}>
        <div className="calendar-overlay-head">
          <div>
            <div className="refresh-kicker">{workshop?.name || 'Workshop'}</div>
            <div className={calendarPage ? 'page-title' : 'modal-title'}>Calendar</div>
            <div className="text-muted fs12">Plan confirmed and provisional workshop time · {fmtDate(calendarStartDate)} to {fmtDate(calendarEndDate)}</div>
          </div>
          <div className="action-row">
            <button type="button" className="btn btn-ghost btn-sm" onClick={toggleCalendarFullscreen} disabled={!fullscreenSupported}>
              {calendarFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
            </button>
            {calendarPage ? (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => { window.location.href = '/' }}>Back to Workshop</button>
            ) : (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowCalendar(false)}>Close</button>
            )}
          </div>
        </div>

        <div className="calendar-toolbar">
          <div className="field compact-field">
            <label>Start Date</label>
            <input type="date" value={calendarStartDate} onChange={e => setCalendarStartDate(e.target.value || todayStr)} />
          </div>
          <div className="calendar-toolbar-actions">
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => shiftCalendar(-calendarRangeDays)}>Previous</button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCalendarStartDate(todayStr)}>Today</button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => shiftCalendar(calendarRangeDays)}>Next</button>
          </div>
          <div className="calendar-toolbar-actions">
            <button
              type="button"
              className={`btn btn-sm ${calendarRangeDays === CALENDAR_RANGE_DAYS.twoWeeks ? 'btn-primary' : 'btn-ghost'}`}
              aria-pressed={calendarRangeDays === CALENDAR_RANGE_DAYS.twoWeeks}
              onClick={() => setCalendarRangeDays(CALENDAR_RANGE_DAYS.twoWeeks)}
            >
              2 weeks
            </button>
            <button
              type="button"
              className={`btn btn-sm ${calendarRangeDays === CALENDAR_RANGE_DAYS.fourWeeks ? 'btn-primary' : 'btn-ghost'}`}
              aria-pressed={calendarRangeDays === CALENDAR_RANGE_DAYS.fourWeeks}
              onClick={() => setCalendarRangeDays(CALENDAR_RANGE_DAYS.fourWeeks)}
            >
              4 weeks
            </button>
          </div>
          <div className="calendar-toolbar-actions">
            {calendarHasGeneral && (
              <label className="calendar-filter-toggle">
                <input type="checkbox" checked={calendarModules.general !== false} onChange={() => toggleCalendarModule('general')} />
                General
              </label>
            )}
            <label className="calendar-filter-toggle">
              <input type="checkbox" checked={calendarModules.suspension !== false} onChange={() => toggleCalendarModule('suspension')} />
              Suspension
            </label>
          </div>
          <div className="field compact-field">
            <label>Technician</label>
            <select value={calendarTechnicianFilter} onChange={e => setCalendarTechnicianFilter(e.target.value)}>
              <option value="all">All technicians</option>
              <option value="unassigned">Unassigned</option>
              {team.map(member => <option key={member.id} value={member.id}>{technicianName(member)}</option>)}
            </select>
          </div>
        </div>

        <div className="calendar-legend">
          <span><i className="calendar-module-dot calendar-module-blue" />General</span>
          <span><i className="calendar-module-dot calendar-module-orange" />Suspension</span>
          {team.map((member, index) => (
            <span key={member.id}><i className="calendar-tech-dot" style={{ background: technicianColour(member, index) }} />{technicianName(member)}</span>
          ))}
          <span><i className="calendar-provisional-swatch" />Provisional request</span>
        </div>

        <div className="capacity-week-strip">
          {capacityWeeks.map(week => (
            <div key={week.weekStart} className={`capacity-week capacity-week-${week.status}`}>
              <div className="capacity-week-head">
                <span>Week {fmtDate(week.weekStart)}</span>
                <strong>{capacityStatusText(week.status)}</strong>
              </div>
              <div className="capacity-week-total">
                <strong>{week.utilisationPercent}%</strong>
                <span>{capacityMinuteLabel(week.confirmedMinutes)} confirmed</span>
                <span>{capacityMinuteLabel(week.provisionalMinutes)} provisional</span>
                <span>{capacityMinuteLabel(week.remainingMinutes)} remaining</span>
              </div>
              <div className="capacity-technicians">
                {week.technicians.map(member => (
                  <div key={member.id} className={`capacity-technician capacity-technician-${member.status}`}>
                    <span><i className="calendar-tech-dot" style={{ background: member.colour }} />{member.name}</span>
                    <em>{capacityMinuteLabel(member.confirmedMinutes)}{member.capacityMinutes === null ? ' / no ceiling' : ` / ${capacityMinuteLabel(member.capacityMinutes)}`}</em>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="calendar-overlay-layout">
          <div className="calendar-main-panel">
            <div className="calendar-grid calendar-overlay-grid">
              {calendarDays.map(date => {
                const label = calendarLabel(date)
                const entries = calendarEntriesByDate[date] || []
                return (
                  <div key={date} className={`calendar-day ${date === todayStr ? 'today' : ''}`}>
                    <div className="calendar-day-head">
                      <span>{label.day}</span>
                      <strong>{label.date}</strong>
                    </div>
                    <div className="calendar-jobs">
                      {entries.length ? entries.map(entry => <CalendarEntry key={`${entry.type}-${entry.id}`} entry={entry} />) : <div className="calendar-empty">No planned work</div>}
                    </div>
                  </div>
                )
              })}
            </div>
            <div className="calendar-list">
              {calendarDays.map(date => {
                const label = calendarLabel(date)
                const entries = calendarEntriesByDate[date] || []
                return (
                  <div key={date} className="calendar-list-day">
                    <div className="calendar-day-head">
                      <span>{label.day}</span>
                      <strong>{label.date}</strong>
                    </div>
                    <div className="calendar-jobs">
                      {entries.length ? entries.map(entry => <CalendarEntry key={`${entry.type}-${entry.id}`} entry={entry} />) : <div className="calendar-empty">No planned work</div>}
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="calendar-under-panel">
              <div className="calendar-side-section calendar-needs-scheduling-section">
                <div className="section-head compact">
                <div className="card-title" style={{ marginBottom: 0 }}>Unscheduled jobs</div>
                  <span className="section-meta">{calendarNeedsScheduling.length}</span>
                </div>
                {calendarNeedsScheduling.length ? calendarNeedsScheduling.slice(0, 12).map(job => (
                  <CalendarSideJobRow key={job.id} job={job} />
                )) : (
                  <div className="muted-empty">No confirmed jobs need scheduling.</div>
                )}
              </div>

              <div className="calendar-side-section">
                <div className="section-head compact">
                <div className="card-title" style={{ marginBottom: 0 }}>Provisional requests</div>
                  <span className="section-meta">{pendingRows.length}</span>
                </div>
                {pendingRows.length ? pendingRows.slice(0, 10).map(request => {
                  const planned = plannedMinutesForBooking(request, { services: calendarServiceRows })
                  return (
                    <button key={request.id} type="button" className="calendar-side-row provisional" onClick={() => setCalendarBookingDetail(request)}>
                      <strong>{request.customer_name || 'Customer'}</strong>
                      <span>{request.preferred_date ? fmtDate(request.preferred_date) : 'No date'} - {planned ? formatPlannedMinutes(planned) : 'Time required'}</span>
                    </button>
                  )
                }) : (
                  <div className="muted-empty">No pending booking requests.</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {calendarBookingDetail && (
          <div className="calendar-booking-detail">
            <div className="section-head compact">
              <div>
                <div className="card-title" style={{ marginBottom: 2 }}>{calendarBookingDetail.customer_name || 'Booking request'}</div>
                <div className="text-muted fs12">{calendarBookingDetail.preferred_date ? fmtDate(calendarBookingDetail.preferred_date) : 'No preferred date'}</div>
              </div>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCalendarBookingDetail(null)}>Close</button>
            </div>
            <div className="fs12 text-muted" style={{ marginBottom: 8 }}>{bookingPreview(calendarBookingDetail)}</div>
            <div className="fs12 text-muted" style={{ marginBottom: 12 }}>
              {calendarBookingDetail.customer_email}{calendarBookingDetail.customer_phone ? ` - ${calendarBookingDetail.customer_phone}` : ''}
            </div>
            <div className="action-row">
              {bookingRequestIsGeneralOnly(calendarBookingDetail) ? (
                <button
                  type="button"
                  className="btn btn-green btn-sm"
                  onClick={() => convertGeneralBookingRequest(calendarBookingDetail)}
                  disabled={busyRequestId === calendarBookingDetail.id || Boolean(generalBookingConversionBlockReason(calendarBookingDetail))}
                >
                  Create General Job
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-green btn-sm"
                  onClick={() => acceptBookingRequest(calendarBookingDetail)}
                  disabled={busyRequestId === calendarBookingDetail.id || Boolean(bookingRequestSuspensionAcceptBlockReason(calendarBookingDetail))}
                >
                  Accept Booking
                </button>
              )}
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => { setShowCalendar(false); openDeclineModal(calendarBookingDetail) }}
                disabled={busyRequestId === calendarBookingDetail.id}
              >
                Decline
              </button>
            </div>
            {bookingRequestReviewReason(calendarBookingDetail) && (
              <div className="alert alert-info" style={{ marginTop: 10 }}>{bookingRequestReviewReason(calendarBookingDetail)}</div>
            )}
          </div>
        )}

        {renderEditJobModal()}
        {renderViewJobModal()}
      </div>
    )
  }

  function renderEditJobModal() {
    if (!editJob) return null
    return (
      <JobModal
        onClose={() => setEditJob(null)}
        onChanged={load}
        onSaved={savedJob => { setEditJob(current => current?.id === savedJob?.id ? { ...current, ...savedJob } : current); load() }}
        onDeleted={() => { setEditJob(null); load() }}
        workshopId={workshopId}
        customers={customers}
        bikes={bikes}
        components={components}
        services={services}
        generalServices={generalJobServices}
        initialData={editJob}
        returnLabel={showCalendar ? 'Back to Calendar' : 'Back to Workshop'}
      />
    )
  }

  function renderViewJobModal() {
    if (!viewJobId || !viewJob) return null
    return (
      <ViewJobModal
        job={viewJob}
        customer={getCust(viewJob)}
        component={getComp(viewJob)}
        bike={getBike(viewJob)}
        workshop={workshop}
        services={services}
        generalServices={generalJobServices}
        onClose={() => setViewJobId(null)}
        onEdit={() => { setViewJobId(null); setEditJob(viewJob) }}
        onChanged={load}
        returnLabel={showCalendar ? 'Back to Calendar' : 'Back to Workshop'}
        onCreateComponent={isSuspensionJob(viewJob) && !getComp(viewJob) && getCust(viewJob) ? () => { setViewJobId(null); createComponentFromJob(viewJob) } : null}
      />
    )
  }

  if (loading) return <div className="empty"><span className="spinner" style={{ width: 20, height: 20 }} /></div>
  if (loadError && !hasLoaded) return (
    <div className="workshop-refresh-page">
      <div className="page-head workshop-refresh-head">
        <div>
          <div className="page-title">Workshop</div>
          <div className="text-muted fs12 mt4">Live jobs, booking requests and service follow-ups.</div>
        </div>
      </div>
      <div className="alert alert-error workshop-refresh-load-error" role="alert">
        <span>{loadError}</span>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setLoading(true); load() }}>Try again</button>
      </div>
    </div>
  )

  if (initialCalendarOpen) return (
    <div className="calendar-refresh-route">
      {loadError && (
        <div className="alert alert-error workshop-refresh-load-error" role="alert">
          <span>{loadError} The open job and unsaved entries have been kept.</span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={load}>Try again</button>
        </div>
      )}
      {renderCalendarOverlay()}
    </div>
  )

  return (
    <div className="workshop-refresh-page">
      <div className="page-head workshop-refresh-head">
        <div>
          <div className="page-title">Workshop</div>
          <div className="text-muted fs12 mt4">Live jobs, booking requests and service follow-ups.</div>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-ghost" onClick={() => setShowCustomerModal(true)} title="Quick add a customer record">Quick Add Customer</button>
          <button type="button" className="btn btn-primary" onClick={() => setShowJobModal(true)} title="Fast intake for a new service job">Fast Job Intake</button>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-error workshop-refresh-load-error" role="alert">
          <span>{loadError} The open job and unsaved entries have been kept.</span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={load}>Try again</button>
        </div>
      )}

      {showLaunchSetup && (
        <div className="card" style={{ marginBottom: 16, borderColor: 'var(--yellow-line)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 12 }}>
            <div>
              <div className="card-title" style={{ marginBottom: 4 }}>Launch Setup</div>
              <div className="text-muted fs12">Finish the booking setup before sharing the widget.</div>
            </div>
            <div className="text-yellow" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', whiteSpace: 'nowrap' }}>
              {launchDoneCount}/{launchSteps.length} ready
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {launchSteps.map(step => (
              <span
                key={step.key}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  border: '1px solid var(--border)',
                  borderRadius: 5,
                  padding: '5px 9px',
                  fontSize: 12,
                  color: step.done ? 'var(--green)' : 'var(--yellow)',
                  background: 'var(--panel)'
                }}
              >
                <strong style={{ color: 'var(--light)' }}>{step.label}</strong>
                {step.done ? 'Done' : 'Todo'}
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            {nextLaunchStep && (
              <button type="button" className="btn btn-primary btn-sm" onClick={nextLaunchStep.action}>
                {nextLaunchStep.actionLabel}
              </button>
            )}
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => window.location.href = '/settings'}>
              Open Settings
            </button>
          </div>
        </div>
      )}

      {componentLinkError && <div className="alert alert-error" style={{ marginBottom: 16 }}>{componentLinkError}</div>}

      <CalendarLaunchCard />
      {showCalendar && renderCalendarOverlay()}

      <CollapsibleCard
        id="workshop-focus"
        title="Workshop focus"
        subtitle="The live action list for today, blockers, handover and reminders."
        meta={`${activeJobs.length} active jobs`}
        tone={focusBlockerCount ? 'orange' : activeJobs.length ? 'blue' : 'muted'}
        className="workshop-refresh-focus"
        defaultOpen
      >
        <div className="focus-summary-strip">
          <FocusTile label="Today" value={todayJobs.length} detail={`${calendarJobCount} in planner`} tone={todayJobs.length ? 'blue' : 'muted'} />
          <FocusTile label="Blocked" value={focusBlockerCount} detail={`${overdueActiveJobs.length} overdue`} tone={focusBlockerCount ? 'orange' : 'muted'} />
          <FocusTile label="Ready" value={focusReadyCount} detail={`${waitingPaymentJobs.length} waiting payment`} tone={focusReadyCount ? 'green' : 'muted'} />
          <FocusTile label="Active value" value={`GBP ${focusValue.toFixed(0)}`} detail={`${activeJobs.length} active jobs`} tone={focusValue ? 'green' : 'muted'} />
        </div>
        <div className="dashboard-filter-tabs" style={{ marginBottom: 14 }}>
          {queueFilterOptions.map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={queueFilter === value ? 'active' : ''}
              onClick={() => setQueueFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="workshop-refresh-queue-grid">
          {visibleDashboardQueues.map(queue => <ActionQueueCard key={queue.key} queue={queue} />)}
        </div>
      </CollapsibleCard>

      <CollapsibleCard
        id="booking-requests"
        title="Booking Requests"
        subtitle="Review customer booking requests before they become draft jobs."
        meta={`${bookingRequests.length} pending`}
        tone={bookingRequests.length ? 'orange' : 'muted'}
        defaultOpen={bookingRequests.length > 0}
      >
        {bookingError && <div className="alert alert-error" style={{ marginBottom: 12 }}>{bookingError}</div>}
        {bookingNotice && <div className="alert alert-success" style={{ marginBottom: 12 }}>{bookingNotice}</div>}
        {bookingRequests.length ? bookingRequests.map(req => {
          const pricing = bookingRequestPricingSummary(req)
          const pricingLabel = bookingRequestHasGeneralService(req) || pricing.pricing_mode !== 'fixed'
            ? pricing.summary_label
            : Number(req.estimated_total || 0) > 0 ? `GBP ${Number(req.estimated_total).toFixed(2)}` : ''
          const isGeneralOnlyRequest = bookingRequestIsGeneralOnly(req)
          const reviewReason = bookingRequestReviewReason(req)
          const canConvertGeneralRequest = isGeneralOnlyRequest && generalWorkshopEnabled && !reviewReason
          const canAcceptSuspensionRequest = !isGeneralOnlyRequest && !reviewReason
          return (
            <div key={req.id} className="booking-request-row">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="fs12 text-white fw7">{req.customer_name} <span className="text-muted fw7">- {fmtDate(req.preferred_date)}</span></div>
                {req.request_source === 'rider_portal' && <div className="fs11 text-green mt4">Rider portal request</div>}
                <div className="fs12 text-muted">{req.customer_email}{req.customer_phone ? ` - ${req.customer_phone}` : ''}</div>
                {req.service_summary && (
                  <div className="fs12 text-muted mt4">
                    Services: {req.service_summary.split('\n').join(' - ')}
                    {pricingLabel ? ` - ${pricingLabel}` : ''}
                  </div>
                )}
                {reviewReason && <div className="fs12 text-yellow mt4">{reviewReason}</div>}
                {(bikeProfile(req) || bikeSetupMeta(req) || req.bike_details || req.component_details) && (
                  <div className="fs12 text-muted mt4">
                    {bikeProfile(req) && <span>Bike: {bikeProfile(req)}</span>}
                    {bikeProfile(req) && bikeSetupMeta(req) && <span> - </span>}
                    {bikeSetupMeta(req) && <span>{bikeSetupMeta(req)}</span>}
                    {(bikeProfile(req) || bikeSetupMeta(req)) && req.bike_details && <span> - </span>}
                    {req.bike_details && <span>Bike details: {req.bike_details}</span>}
                    {(bikeProfile(req) || bikeSetupMeta(req) || req.bike_details) && req.component_details && <span> - </span>}
                    {req.component_details && <span>Component: {req.component_details.split('\n').filter(Boolean).join(' - ')}</span>}
                  </div>
                )}
                {req.notes && <div className="fs12 text-muted mt4">Notes: {req.notes}</div>}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {canConvertGeneralRequest ? (
                  <button
                    type="button"
                    className="btn btn-green btn-sm"
                    onClick={() => convertGeneralBookingRequest(req)}
                    disabled={busyRequestId === req.id}
                  >
                    {busyRequestId === req.id ? 'Creating...' : 'Create General Job'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-green btn-sm"
                    onClick={() => acceptBookingRequest(req)}
                    disabled={busyRequestId === req.id || !canAcceptSuspensionRequest}
                  >
                    {!canAcceptSuspensionRequest ? 'Review Only' : busyRequestId === req.id ? 'Accepting...' : 'Accept Booking'}
                  </button>
                )}
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => openDeclineModal(req)} disabled={busyRequestId === req.id}>
                  Decline
                </button>
              </div>
            </div>
          )
        }) : (
          <div className="muted-empty">No pending booking requests.</div>
        )}
      </CollapsibleCard>

      {/* Rejected bookings */}
      <div className="booking-history-grid">
      <div className="card compact-card" style={{ marginBottom: 16 }}>
        <div className="section-head compact" style={{ marginBottom: showRejectedHistory ? 10 : 0 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>Rejected Bookings</div>
          <div className="action-row">
            <span className="section-meta">{rejectedBookings.length} recent</span>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowRejectedHistory(v => !v)} disabled={!rejectedBookings.length}>
              {showRejectedHistory ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>
        {showRejectedHistory && (rejectedBookings.length ? rejectedBookings.map(req => (
          <div key={req.id} className="booking-request-row">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="fs12 text-white fw7">
                {req.customer_name} <span className="text-muted fw7">- {fmtDate(req.preferred_date)}</span>
              </div>
              <div className="fs12 text-muted">{req.customer_email}{req.customer_phone ? ` - ${req.customer_phone}` : ''}</div>
              <div className="fs12 text-muted mt4">{bookingPreview(req)}</div>
              {req.response_reason && <div className="fs12 text-muted mt4">Reason: {req.response_reason}</div>}
              {req.proposed_date && <div className="fs12 text-yellow mt4">Proposed new date: {fmtDate(req.proposed_date)}</div>}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="fs12 fw7" style={{ color: statusColor(req.status), textTransform: 'capitalize' }}>Rejected</div>
              <div className="fs11 text-muted">{req.responded_at ? fmtDate(req.responded_at.slice(0, 10)) : req.created_at ? fmtDate(req.created_at.slice(0, 10)) : ''}</div>
              {req.response_sent_at && <div className="fs11 text-green">Email sent</div>}
            </div>
          </div>
        )) : (
          <div className="muted-empty">No rejected bookings yet.</div>
        ))}
      </div>

      {/* Accepted booking history */}
      <div className="card compact-card" style={{ marginBottom: 16 }}>
        <div className="section-head compact" style={{ marginBottom: showAcceptedHistory ? 10 : 0 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>Accepted Booking History</div>
          <div className="action-row">
            <span className="section-meta">{acceptedBookingHistory.length} recent</span>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowAcceptedHistory(v => !v)} disabled={!acceptedBookingHistory.length}>
              {showAcceptedHistory ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>
        {showAcceptedHistory && (acceptedBookingHistory.length ? acceptedBookingHistory.map(req => (
          <div key={req.id} className="booking-request-row">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="fs12 text-white fw7">
                {req.customer_name} <span className="text-muted fw7">- {fmtDate(req.preferred_date)}</span>
              </div>
              <div className="fs12 text-muted">{req.customer_email}{req.customer_phone ? ` - ${req.customer_phone}` : ''}</div>
              <div className="fs12 text-muted mt4">{bookingPreview(req)}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="fs12 fw7" style={{ color: statusColor(req.status), textTransform: 'capitalize' }}>{req.status}</div>
              <div className="fs11 text-muted">{req.created_at ? fmtDate(req.created_at.slice(0, 10)) : ''}</div>
            </div>
          </div>
        )) : (
          <div className="muted-empty">No accepted bookings yet.</div>
        ))}
      </div>
      </div>

      {(customers.length === 0 || components.length === 0 || jobs.length === 0) && (
        <div className="card" style={{ marginBottom: 16, borderColor: 'var(--yellow-line)', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'var(--yellow)' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--white)', marginBottom: 4 }}>Get started with Rebound</div>
              <div className="text-muted fs12">Complete these three steps to log your first service job.</div>
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--yellow)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
              {[customers.length > 0, components.length > 0, jobs.length > 0].filter(Boolean).length}/3 done
            </div>
          </div>

          {[
            {
              done: customers.length > 0,
              num: '1',
              title: 'Add your first customer',
              desc: 'Create a customer record with their name and contact details.',
              action: () => setShowCustomerModal(true),
              actionLabel: 'Add Customer'
            },
            {
              done: components.length > 0,
              num: '2',
              title: 'Register a suspension component',
              desc: 'Add a fork, shock, or dropper post for the customer. Use serial number lookup for Fox and RockShox.',
              action: () => window.location.href = '/customers',
              actionLabel: 'Go to Customers',
              locked: customers.length === 0
            },
            {
              done: jobs.length > 0,
              num: '3',
              title: 'Log your first service job',
              desc: 'Record a complete service sheet including settings, oil volumes, torques, QC, and invoice.',
              action: () => setShowJobModal(true),
              actionLabel: 'New Service Job',
              locked: components.length === 0
            }
          ].map(step => (
            <div key={step.num} style={{
              display: 'flex', gap: 14, alignItems: 'flex-start',
              padding: '12px 0',
              borderTop: '1px solid var(--border)',
              opacity: step.locked ? 0.45 : 1
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700,
                background: step.done ? 'var(--green)' : 'var(--panel)',
                border: `2px solid ${step.done ? 'var(--green)' : 'var(--border)'}`,
                color: step.done ? 'var(--text)' : 'var(--muted)'
              }}>
                {step.done ? '✓' : step.num}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: 13, fontWeight: 700,
                  color: step.done ? 'var(--muted)' : 'var(--white)',
                  textDecoration: step.done ? 'line-through' : 'none',
                  marginBottom: 2
                }}>{step.title}</div>
                {!step.done && <div className="text-muted fs12">{step.desc}</div>}
              </div>
              {!step.done && !step.locked && (
                <button type="button" className="btn btn-primary btn-sm" onClick={step.action} style={{ flexShrink: 0 }}>
                  {step.actionLabel}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Stats */}
      <div className="grid-4" style={{ marginBottom: 16 }}>
        <div className="stat"><span className="stat-val">{customers.length}</span><span className="stat-label">Customers</span></div>
        <div className="stat"><span className="stat-val">{jobs.length}</span><span className="stat-label">Service Jobs</span></div>
        <div className="stat"><span className="stat-val">£{rev.toFixed(0)}</span><span className="stat-label">Revenue</span></div>
        <div className="stat"><span className="stat-val text-green">£{margin.toFixed(0)}</span><span className="stat-label">Gross Margin ({mpct}%)</span></div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
        <MiniBreakdownCard
          title="Job Stage Breakdown"
          subtitle="What is currently holding work up"
          rows={workflowBreakdown}
        />
        <MiniBreakdownCard
          title="Payment Breakdown"
          subtitle={`${waitingPaymentCount} waiting for payment`}
          rows={paymentBreakdown}
        />
      </div>

      <CollapsibleCard
        id="operations-detail"
        title="Operations Detail"
        subtitle="Upcoming service reminders and follow-up actions."
        meta={`${serviceDueAll.length} due`}
        tone={serviceDueAll.length ? 'orange' : 'muted'}
        defaultOpen={false}
      >
      <div className="dashboard-ops-grid">
      <div style={{ background: 'var(--black)', border: '1px solid var(--border)', borderRadius: 6, padding: 14 }}>
        <div className="section-head compact">
          <div className="card-title" style={{ marginBottom: 0 }}>Upcoming Service Reminders</div>
          <span className="section-meta">{serviceDueAll.length} next 30 days</span>
        </div>
        {reminderNotice && <div className="alert alert-success" style={{ marginBottom: 10 }}>{reminderNotice}</div>}
        {reminderError && <div className="alert alert-error" style={{ marginBottom: 10 }}>{reminderError}</div>}
        {serviceDueJobs.length ? serviceDueJobs.map(j => <ReminderDueRow key={`due-${j.id}`} job={j} />) : (
          <div className="muted-empty">No services due in the next 30 days.</div>
        )}
      </div>

      </div>
      </CollapsibleCard>

      {/* Lower panels */}
      <CollapsibleCard
        id="performance-snapshot"
        title="Performance Snapshot"
        subtitle="Service timing, recent work and customer spend."
        meta={`${recentJobs.length} recent`}
        tone="green"
        defaultOpen={false}
      >
      <div className="dashboard-lower-grid">
        <div style={{ background: 'var(--black)', border: '1px solid var(--border)', borderRadius: 6, padding: 14 }}>
          <div className="card-title">Avg Service Times</div>
          {timeKpis.length ? timeKpis.map(([label, mins]) => (
            <div key={label} className="job-row">
              <span className="fs12 text-muted">{label}</span>
              <span className="fs12 text-white fw7">{fmtDuration(mins)}</span>
            </div>
          )) : <div className="muted-empty">No timed jobs yet.</div>}
        </div>

        <div style={{ background: 'var(--black)', border: '1px solid var(--border)', borderRadius: 6, padding: 14 }}>
          <div className="card-title">Recent Jobs</div>
          {recentJobs.length ? recentJobs.map(j => {
            const comp = getComp(j); const cust = getCust(j)
            const financials = jobFinancials(j)
            const status = jobStatus(j)
            return (
              <div key={j.id} className="job-row">
                <div>
                  <div className="fs12 text-white">{jobDisplayTitle(j)}</div>
                  <div className="fs12 text-muted">{cust ? cust.first_name + ' ' + cust.last_name : 'Unknown customer'} - {fmtDate(j.date)}{j.duration_mins ? ' - ' + fmtDuration(j.duration_mins) : ''}</div>
                  <div className="action-row">{progressChips(j, 3)}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <StatusPill as="div" tone={status.tone} style={{ marginBottom: 4 }}>{status.label}</StatusPill>
                  <div className="fs12 text-white fw7">£{financials.totalCost.toFixed(2)}</div>
                  <div className="fs11 text-green">{financials.marginPercent.toFixed(0)}%</div>
                </div>
              </div>
            )
          }) : <div className="muted-empty">No jobs yet.</div>}
        </div>

        <div style={{ background: 'var(--black)', border: '1px solid var(--border)', borderRadius: 6, padding: 14 }}>
          <div className="card-title">Top Customers by Spend</div>
          {bySpend.length ? bySpend.map(c => (
            <div key={c.name} className="job-row">
              <div>
                <div className="fs12 text-white">{c.name}</div>
                <div className="fs12 text-muted">{c.count} job{c.count !== 1 ? 's' : ''}</div>
              </div>
              <div className="fs12 text-green fw7">£{c.spend.toFixed(2)}</div>
            </div>
          )) : <div className="muted-empty">No customers yet.</div>}
        </div>
      </div>
      </CollapsibleCard>

      {declineRequest && (
        <div className="modal-overlay">
          <form className="modal" onSubmit={declineBookingRequest}>
            <div className="modal-header">
              <div className="modal-title">Decline Booking</div>
              <button className="close-btn" type="button" onClick={closeDeclineModal} disabled={declining}>x</button>
            </div>

            <div className="alert alert-info" style={{ marginBottom: 16 }}>
              {declineRequest.customer_name} requested {fmtDate(declineRequest.preferred_date)}.
            </div>
            {declineError && <div className="alert alert-error" style={{ marginBottom: 14 }}>{declineError}</div>}

            <div className="field">
              <label>Reason</label>
              <textarea
                value={declineForm.reason}
                onChange={e => setDeclineField('reason', e.target.value)}
                placeholder="e.g. That date is fully booked, or the selected service is not available."
                required
              />
            </div>

            <div className="field">
              <label>Proposed New Date</label>
              <input
                type="date"
                value={declineForm.proposedDate}
                min={todayStr}
                onChange={e => setDeclineField('proposedDate', e.target.value)}
              />
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button className="btn btn-ghost" type="button" onClick={closeDeclineModal} disabled={declining}>Cancel</button>
              <button className="btn btn-red" type="submit" disabled={declining || busyRequestId === declineRequest.id}>
                {declining ? 'Declining...' : 'Decline and Email Customer'}
              </button>
            </div>
          </form>
        </div>
      )}

      {showCustomerModal && (
        <CustomerModal onClose={() => setShowCustomerModal(false)} onSaved={() => { setShowCustomerModal(false); load() }} workshopId={workshopId} />
      )}
      {compModal && (
        <ComponentModal
          workshopId={workshopId}
          customerId={compModal.customerId}
          prefillData={compModal.prefillData}
          onClose={() => setCompModal(null)}
          onSaved={handleComponentSaved}
          onDeleted={() => { setCompModal(null); load() }}
        />
      )}
      {showJobModal && (
        <NewJobIntakeModal
          userId={profile?.id}
          onClose={() => setShowJobModal(false)}
          onSaved={(job) => { setShowJobModal(false); if (job?.id) setViewJobId(job.id); load() }}
          workshopId={workshopId}
          customers={customers}
          bikes={bikes}
          components={components}
          services={services}
          preCustomerId={showJobModal && showJobModal.customerId ? showJobModal.customerId : null}
          preComponentId={showJobModal && showJobModal.componentId ? showJobModal.componentId : null}
        />
      )}
      {!showCalendar && renderEditJobModal()}
      {!showCalendar && renderViewJobModal()}
    </div>
  )
}
